import { Section } from '@/store/section.interface';
import { Context } from '@/store/context.interface';
import { DTOLanguageEnabled, LanguageEnabled, LanguageTranslation } from '@/store/language.interface';
import { StoredFile } from './common.interface';
import { HomepageItem } from './homepageItem.interface';
import { constant } from '@/constant';

export type ParticipationMode = number;
export type GuidedConsultationResultsMode = number;

export interface VisibilityCondition {
  id: string;
  sourceFormId: number;
  operator: 'equals';
  valueIds?: number[];
}

export interface VisibilityRuleSet {
  id: string;
  targetType: 'form';
  targetId: number;
  match: 'all';
  conditions: VisibilityCondition[];
}

export interface GuidedConsultationVisibility {
  ruleSets: VisibilityRuleSet[];
}

export interface GuidedConsultationOptions {
  resultsMode: GuidedConsultationResultsMode;
  visibility: GuidedConsultationVisibility;
}

export interface FormMeta {
  title: LanguageTranslation;
  sectionId: number;
  sectionOrderNum: number;
}

export interface ParticipationSummary {
  visibleFormIds: number[];
  hiddenFormIds: number[];
  mandatoryFormIds: number[];
  answeredFormIds: number[];
  missingMandatoryFormIds: number[];
  skippedFormIds: number[];
  completionPercentage: number;
  canComplete: boolean;
  evaluatedAt: string;
  formMeta: Record<number, FormMeta>;
}

export interface ParticipationState {
  id: number;
  status: number;
  summary: ParticipationSummary;
}

export interface ConsultationOptions {
  citizen_proposal?: boolean;
  map?: boolean;
  anonymous_comments?: boolean;
  mailingList?: boolean;
  show_private_everyone?: boolean;
  automatic_publish?: boolean;
  guided_consultation?: GuidedConsultationOptions;
}

export interface DTOConsultation extends DTOLanguageEnabled {
  id: number;
  slug: string;
  begin_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
  status: number;
  type: number;
  participation_mode: ParticipationMode;
  community_id: number;
  order_num: number;
  banner_file: StoredFile;
  title: LanguageTranslation;
  description: LanguageTranslation;
  summary: ConsultationSummary;
  welcome_video: LanguageTranslation | null;
  welcome_video_type: string | null;
  is_allowed?: boolean;
  options: ConsultationOptions;
  attachmentsOpened: boolean | null;
  projects: ConsultationProject[];
  subscriber_mailing_list: ConsultationSubscriberMailingList | null;
  conclusion_message: ConclusionMessage;
}

export interface Consultation extends HomepageItem, LanguageEnabled {
  id: number;
  createdAt: string;
  updatedAt: string;
  communityId: number;
  orderNum: number;
  section: Section[];
  context: Context[];
  summary: ConsultationSummary;
  welcomeVideo: LanguageTranslation | null;
  welcomeVideoType: string | null;
  participationMode: ParticipationMode;
  options: ConsultationOptions;
  attachmentsOpened: boolean | null;
  slug: string;
  projects: ConsultationProject[];
  subscriberMailingList: ConsultationSubscriberMailingList | null;
  conclusionMessage: ConclusionMessage;
}

export interface ConsultationProject {
  id: number;
  title: LanguageTranslation;
  slug: string;
}

interface ConsultationSummary {
  sections?: number;
  polls: number;
  surveys: number;
  votes: number;
  user_participation?: number;
}

interface ConsultationSubscriberMailingList {
  isUserSubscribed: boolean;
  subscriberCount: number;
}

export interface ConclusionMessage {
  enabled: boolean;
  content: LanguageTranslation;
}

export function parseConsultation(consultationDto: DTOConsultation): Consultation {
  return {
    itemType: constant.HomePageItem.Type.Consultation,
    id: consultationDto.id,
    slug: consultationDto.slug,
    status: consultationDto.status,
    participationMode: consultationDto.participation_mode,
    beginDate: consultationDto.begin_date ? new Date(consultationDto.begin_date) : null,
    endDate: consultationDto.end_date ? new Date(consultationDto.end_date) : null,
    type: consultationDto.type,
    communityId: consultationDto.community_id,
    bannerFile: consultationDto.banner_file,
    orderNum: consultationDto.order_num,
    title: consultationDto.title,
    description: consultationDto.description,
    summary: consultationDto.summary,
    section: [],
    context: [],
    createdAt: consultationDto.created_at,
    updatedAt: consultationDto.updated_at,
    welcomeVideo: consultationDto.welcome_video,
    welcomeVideoType: consultationDto.welcome_video_type,
    isAllowed: consultationDto.is_allowed,
    options: consultationDto.options,
    attachmentsOpened: consultationDto.attachmentsOpened,
    projects: consultationDto.projects,
    subscriberMailingList: consultationDto.subscriber_mailing_list,
    languageEnabled: consultationDto.language_enabled,
    conclusionMessage: consultationDto.conclusion_message,
  };
}
